import { Saga } from 'redux-saga'
import {
  all,
  put,
  call,
  select,
  takeLatest,
  ActionPattern,
} from 'redux-saga/effects'
import { parse } from 'query-string'

import { secureSanitize } from '../../utils/sanitizeUtils'
import { actions, selectors } from '..'
import ApiSagas from '../api/sagas'
import { Recipes } from '../../graphql/Services'

export default class RecipesSagas {
  static *init(context: any) {
    const {
      query: { page = 1 },
    } = context

    const sanitizedPage = secureSanitize(page, true)

    const toBeParsed = context?.asPath
      ? context.asPath
      : context?.req?.url?.split('?')?.pop() ?? ''

    const qs = parse(toBeParsed, { arrayFormat: 'comma' })

    const filters = {
      courseType: qs?.courseType
        ? Array.isArray(qs?.courseType)
          ? qs?.courseType
          : [qs?.courseType]
        : [],
      diet: qs?.diet ?? 'ALL',
      cookingTime: qs?.cookingTime
        ? Array.isArray(qs?.cookingTime)
          ? qs?.cookingTime
          : [qs?.cookingTime]
        : [],
      query: qs?.query ? secureSanitize(qs.query as string, true) : undefined,
      recipeMainComponent: qs?.recipeMainComponent
        ? Array.isArray(qs?.recipeMainComponent)
          ? qs?.recipeMainComponent
          : [qs?.recipeMainComponent]
        : [],
    }

    yield put(actions.recipes.setRecipesFilters(filters))
    yield put(actions.recipes.setRecipesPage(parseInt(sanitizedPage, 10)))

    return yield call(RecipesSagas.onLoadRecipes)
  }

  static *loadRecipes(page: number) {
    const apiFilters = yield select(selectors.recipes.apiFilters)

    const rs = yield call(
      ApiSagas.query,
      Recipes.queries.recipeSearch({
        query: apiFilters?.query,
        first: 9,
        page,
        express: apiFilters?.cookingTime?.includes('express') || null, //backend wants null instead of false
        type:
          apiFilters?.courseType?.length > 0 ? apiFilters?.courseType : null,
        regime: apiFilters?.diet !== 'none' ? apiFilters?.diet : null,
      })
    )

    yield put(actions.recipes.setRecipesPage(page))

    return rs
  }

  static *onLoadRecipes() {
    const page = yield select(selectors.recipes.page)
    const { data: result } = yield call(RecipesSagas.loadRecipes, page)

    yield put(
      actions.recipes.setHasMorePages(result.paginatorInfo.hasMorePages)
    )

    yield put(actions.recipes.setRecipes(result?.data))
    yield put(actions.recipes.setLoadedPages([page]))

    if (!result) {
      return { error: 404 }
    }
  }

  static *onLoadPreviousRecipes() {
    const loadedPages = yield select(selectors.recipes.loadedPages)
    const lastPage = yield select(selectors.recipes.firstLoadedPage)
    const page = lastPage - 1
    const { data: result } = yield call(RecipesSagas.loadRecipes, page)

    yield put(actions.recipes.prependRecipes(result?.data))
    yield put(actions.recipes.setLoadedPages(loadedPages.concat([page])))

    if (!result) {
      return { error: 404 }
    }
  }

  static *onLoadMoreRecipes() {
    const loadedPages = yield select(selectors.recipes.loadedPages)
    const lastPage = yield select(selectors.recipes.lastLoadedPage)
    const page = lastPage + 1
    const { data: result } = yield call(RecipesSagas.loadRecipes, page)

    yield put(
      actions.recipes.setHasMorePages(result.paginatorInfo.hasMorePages)
    )

    yield put(actions.recipes.appendRecipes(result?.data))
    yield put(actions.recipes.setLoadedPages(loadedPages.concat([page])))

    if (!result) {
      return { error: 404 }
    }
  }

  static *loop() {
    yield all([
      takeLatest(
        actions.recipes.loadMoreRecipesPage as ActionPattern,
        this.onLoadMoreRecipes as Saga
      ),
      takeLatest(
        actions.recipes.loadPreviousRecipesPage as ActionPattern,
        this.onLoadPreviousRecipes as Saga
      ),
      takeLatest(
        actions.recipes.loadRecipes as ActionPattern,
        this.onLoadRecipes as Saga
      ),
    ])
  }
}
